import type { TicketLayout as GQLItem, TicketLayoutConnection as GQLConnection } from '../../../generated/internal.graphql';
import { take } from '../../../util/takePayload';
import * as GqlConnection from '../../connection/GqlConnection';
import * as PageInfo from '../../connection/PageInfo';

/**
 * A printable ticket layout as the superadmin screens read and write it on the internal
 * (VP staff) schema.
 *
 * Replaces the legacy `src/api/TicketLayout/` module (legacy interface: `apiClient`,
 * `generated/graphql`, nested under `app { … }`).
 */

export type TicketLayout = {
  id: number;
  name: string;
  fglString: string;
  layout: Record<string, unknown>;
  default: boolean;
};

export type TicketLayoutConnection = GqlConnection.GqlConnection<TicketLayout>;

/**
 * Mirrors the internal `TicketLayoutAttributes` input. `id` is a **number**: the legacy input
 * typed it `string`, which never matched `TicketLayoutAttributes.id: Int` on any schema.
 */
export type TicketLayoutInput = {
  id?: number;
  name?: string;
  fglString?: string;
  layout?: Record<string, unknown>;
  default?: boolean;
};

/**
 * `fglString` and `layout` are nullable on every schema, the legacy one included — so a `null`
 * has always been able to reach the screens, where `PrintService.renderTemplate` and
 * `tokenUseStatistics` are typed for a `string` and `VPFglRenderer` dereferences
 * `ticketLayout.dpi` unguarded. Substituting the empty value is therefore a deliberate
 * hardening, not a compatibility shim: `{}` still renders `NaN` dimensions, but it fails soft
 * where `null` throws, and it keeps every field component unchanged.
 */
export const fromGql = (item: GQLItem): TicketLayout => ({
  ...take<TicketLayout>(item, 'id', 'name', 'default'),
  fglString: item.fglString ?? '',
  layout: (item.layout ?? {}) as Record<string, unknown>,
});

/**
 * Mapped by hand rather than through `GqlConnection.fromGql`, for two reasons the shared helper
 * cannot cover:
 *
 * - `edges` and `node` are both nullable on the schema. graphql-ruby does not emit those nulls
 *   for this resolver, but casting them away would make a mapper throw
 *   "Cannot read properties of null" — which the caller's `try`/`catch` would then report as an
 *   opaque failure. This module fails soft on schema nullability everywhere else; do it here too.
 * - The query selects `edges` only, so the helper would leave `nodes` permanently `[]` while
 *   `GqlConnection<T>` promises a populated array. Deriving it from the edges keeps that promise
 *   instead of quietly handing a later consumer an empty list.
 */
export const fromGqlConnection = (connection: GQLConnection): TicketLayoutConnection => {
  const edges = (connection.edges ?? [])
    .filter(edge => Boolean(edge?.node))
    .map(edge => ({ cursor: edge!.cursor, node: fromGql(edge!.node as GQLItem) }));

  return {
    edges,
    nodes: edges.map(edge => edge.node),
    pageInfo: PageInfo.fromGql(connection.pageInfo),
  };
};
